import { useEffect, useState } from "react"
import { Button } from "./ui/Button"
import { Icon } from "./icons/Icon"
import clsx from "clsx"
import { useAtomValue, useSetAtom } from "jotai"
import { leftExpandedPanelAtom } from "@/store/ui-views-state"
import {
	ImageResponseDTO,
	SampleCollectionDTO,
	SampleResponseDTO,
} from "@/store/colorMatch/color-match-service"
import {
	colorMatchImagesAtom,
	importColorMatchImageFromSample,
} from "@/store/colorMatch/color-match-state"
import { Spinner } from "./ui/Spinner"
import { analytics } from "@/telemetry/amplitude"
import { processImageUrl } from "@/utils/process-image-url"
import { fetchWithNetworkBehavior } from "@/api/fetch-with-network-behavior"
import { pixiesetAssetBaseURIs } from "@/utils/pixieset-urls"
import { useTranslation } from "react-i18next"

const sampleCategoryNameKeys: Record<string, string> = {
	All: "colorMatchPanel.sampleCategoryAll",
	Film: "colorMatchPanel.sampleCategoryFilm",
	Wedding: "colorMatchPanel.sampleCategoryWedding",
}

export const ColorMatchSamplesPanel = () => {
	const { t } = useTranslation("components")
	const samplesURL = `${pixiesetAssetBaseURIs.imagesPe}/pn/assets/colortransfer/samples/manifest.json`

	const setLeftExpandedPanel = useSetAtom(leftExpandedPanelAtom)
	const [samples, setSamples] = useState<SampleCollectionDTO[]>([])

	const tabs = samples.map((sample) => sample.name)
	const [tabIndex, setTabIndex] = useState<number>(0)
	const currentCollection = samples[tabIndex]
	const getCollectionLabel = (name: string) => {
		const key = sampleCategoryNameKeys[name]
		return key ? t(key) : name
	}

	const fetchSamples = async () => {
		const response: SampleResponseDTO = await fetchWithNetworkBehavior(samplesURL, undefined, {
			networkIntent: "active",
		}).then((res) => res.json())

		if (!Array.isArray(response.collections)) {
			throw new Error("Collections is not an array")
		}

		const all = response.collections.reduce<ImageResponseDTO[]>((acc, collection) => {
			return acc.concat(collection.images)
		}, [])
		const allCollection: SampleCollectionDTO = { name: "All", images: all }
		setSamples([allCollection, ...response.collections])
	}

	useEffect(() => {
		void fetchSamples()
	}, [])

	return (
		<div className="flex w-full flex-col items-start space-y-0">
			<div className="sticky top-0 z-10 flex w-full flex-col items-start space-y-0 bg-base-800">
				<div className="flex w-full justify-end p-2">
					<Button
						trackingLabel="close"
						variant="custom"
						size="custom"
						onClick={() => setLeftExpandedPanel(null)}
					>
						<Icon name="right" className="hover:text-base-400" />
					</Button>
				</div>
				<div role="tablist" className="flex flex-row space-x-2 px-3 text-xs">
					{tabs.map((tab, index) => (
						<div
							role="tab"
							aria-selected={index === tabIndex}
							tabIndex={0}
							className={clsx(
								index === tabIndex ? "text-base-100" : "text-base-400",
								"pb-2",
							)}
							key={tab}
							onClick={() => setTabIndex(index)}
						>
							{getCollectionLabel(tab)}
						</div>
					))}
				</div>
			</div>
			<div className="grid grid-cols-2 gap-2 px-3 py-2">
				{currentCollection?.images.map((image) => (
					<SampleImageView key={image.distName} sample={image} />
				))}
			</div>
		</div>
	)
}

const SampleImageView = (props: { sample: ImageResponseDTO }) => {
	const { t } = useTranslation("components")
	const { data: colorMatchImages } = useAtomValue(colorMatchImagesAtom)
	const isAdded = colorMatchImages.find(
		(image) => image.contentDigest === props.sample.contentHash,
	)
	const [isHovering, setIsHovering] = useState(false)
	const [isAdding, setIsAdding] = useState(false)

	const addImage = async () => {
		if (isAdded || isAdding) return
		setIsAdding(true)
		await importColorMatchImageFromSample(props.sample)
		setIsAdding(false)
		analytics.trackEvent({
			label: "add_sample",
			type: "click",
			value: props.sample.distName,
		})
	}
	return (
		<div
			className="relative flex items-center justify-center"
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
		>
			<img
				src={processImageUrl({
					url: props.sample.url,
					shortestSidePxSizeOfResize: 500,
				})}
				alt={props.sample.displayName}
				className="aspect-square rounded-md bg-base-700 object-cover"
				crossOrigin="anonymous"
			/>

			{(isHovering || isAdded) && (
				<div className="absolute right-1 top-1">
					<div
						className={clsx(
							isAdded ? "bg-base-600" : "bg-accent-600 hover:bg-accent-700",
							"relative flex items-center justify-center self-center rounded-md px-2 py-1 text-xs",
						)}
						onClick={() => void addImage()}
					>
						<div className={clsx(isAdding && "invisible")}>
							{isAdded
								? t("colorMatchPanel.sampleAdded")
								: t("colorMatchPanel.sampleAdd")}
						</div>
						{isAdding && <Spinner className="absolute z-10" size={8} />}
					</div>
				</div>
			)}
		</div>
	)
}
